/**
 * Gateway Response Mapper
 * Validates the `GET /api/usm/v1/users/me` response (`ApiResponse<CurrentUserResponse>`) and maps
 * it to the `User` entity.
 *
 * NOT `/auth/me` — that endpoint returns a flat, unwrapped session object with `UserId` and no
 * `id` / `status` / `createdAt`, which the schemas below reject. The old comment here said
 * `/auth/me` and is the likely source of that error propagating into the AXF-1013 spec.
 */
import { z } from 'zod'
import type { User } from '@domain/entities'
import { UserStatus } from '@domain/entities'
import { createLoggerWithPrefix } from '@shared/utils/logger'
import { tenantRoleSchema } from '@application/dto/user.dto'

const logger = createLoggerWithPrefix('gatewayMapper')

const gatewayUserDataSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  displayName: z.string(),
  avatarUrl: z.string().nullable().optional(),
  tenantRole: tenantRoleSchema,
  status: z.string(),
  lastLoginAt: z.string().nullable().optional(),
  isSuperAdmin: z.boolean(),
  isDevAdmin: z.boolean().default(false),
  tenantId: z.string().nullable().optional(),
  entraObjectId: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string().nullable().optional(),
})

type GatewayUserData = z.infer<typeof gatewayUserDataSchema>

const gatewayResponseWrapperSchema = z.object({
  success: z.boolean(),
  data: gatewayUserDataSchema,
  message: z.string().nullable().optional(),
  errors: z.array(z.unknown()).nullable().optional(),
  correlationId: z.string().nullable().optional(),
  tenantId: z.string().nullable().optional(),
  timestamp: z.string().optional(),
})

const mapStatusToEnum = (status: string): UserStatus => {
  switch (status.toLowerCase()) {
    case 'active': return UserStatus.ACTIVE
    case 'inactive': return UserStatus.INACTIVE
    case 'suspended': return UserStatus.SUSPENDED
    default:
      logger.warn(`Unknown status "${status}", defaulting to ACTIVE`)
      return UserStatus.ACTIVE
  }
}

const mapValidatedDataToUser = (userData: GatewayUserData): User => {
  return {
    id: userData.id,
    email: userData.email,
    displayName: userData.displayName,
    avatarUrl: userData.avatarUrl || undefined,
    tenantRole: userData.tenantRole ?? undefined,
    status: mapStatusToEnum(userData.status),
    isSuperAdmin: userData.isSuperAdmin,
    isDevAdmin: userData.isDevAdmin,
    identityLinked: false,
    tenantId: userData.tenantId || undefined,
    lastLoginAt: userData.lastLoginAt || undefined,
    createdAt: userData.createdAt,
    updatedAt: userData.updatedAt || undefined,
  }
}

/**
 * Map Gateway /auth/me response to User entity
 */
export const mapGatewayResponseToUser = (response: unknown): User | null => {
  if (!response || typeof response !== 'object') {
    logger.error('Response is null, undefined, or not an object')
    return null
  }

  const responseObj = response as Record<string, unknown>

  // Try wrapped response: { success, data: {...}, ... }
  const wrapperResult = gatewayResponseWrapperSchema.safeParse(response)
  if (wrapperResult.success) {
    return mapValidatedDataToUser(wrapperResult.data.data)
  }

  // Fallback: extract data field or treat as direct user object
  let rawUserData: unknown
  if (responseObj.data && typeof responseObj.data === 'object') {
    rawUserData = responseObj.data
  } else if ('id' in responseObj || 'email' in responseObj) {
    rawUserData = responseObj
  } else {
    logger.error('Response does not contain user data')
    return null
  }

  const dataResult = gatewayUserDataSchema.safeParse(rawUserData)
  if (!dataResult.success) {
    logger.error('Gateway user data validation failed:', dataResult.error.format())
    return null
  }

  return mapValidatedDataToUser(dataResult.data)
}
